import { useState, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import {
    useProduct,
    useSetProductFlag,
    useSetProductApproval,
    useDeleteProduct,
    useRemoveFromCategory,
    AddToSetModal,
    AddVariantModal,
    ManageVariantsModal,
    AddToCategoryModal,
    SetDiscountModal,
    EditProductModal,
    RejectProductModal,
    ProductFlagType,
    productThumbSrc,
} from '@modules/products';
import { useCategories } from '@modules/categories';
import { useSections } from '@modules/sections';
import { useCustomSet } from '@modules/custom-sets';
import { useFilterSet } from '@modules/filter-sets';
import { Breadcrumbs, type BreadcrumbItem } from '@components';
import { BackButton, Button, CachedImage, DoubleCheckModal, RefetchButton } from '@shared/ui';
import ProductPrice from '@modules/products/components/ProductCard/ProductPrice';
import ProductBadges from '@modules/products/components/ProductCard/ProductBadges';
import '@shared/styles/page.css';
import styles from './ProductPage.module.css';
import { Loader } from '@mantine/core';
import {
    IoAdd,
    IoCartOutline,
    IoChevronDown,
    IoChevronUp,
    IoCreateOutline,
    IoEyeOutline,
    IoPricetagOutline,
} from 'react-icons/io5';
import { sellersApi } from '@modules/sellers/api/sellers.api';

function ProductField({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) {
    return (
        <div className={styles.row}>
            <div className={styles.rowLabel}>{label}</div>
            <div className={styles.rowValue}>{children}</div>
        </div>
    );
}

export default function ProductPage() {
    const { productId } = useParams<{ productId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const productIdNum = productId ? parseInt(productId, 10) : undefined;

    const { data: product, isLoading: productLoading, error: productError, refetch: refetchProduct } = useProduct(productIdNum!);
    const { data: categories = [] } = useCategories();
    const { data: sections = [] } = useSections();
    const queryClient = useQueryClient();

    const { isSellerAdmin, isSuperAdmin } = useMemo(() => {
        try {
            const raw = localStorage.getItem('luxus_admin');
            if (!raw) return { isSellerAdmin: false, isSuperAdmin: false };
            const p = JSON.parse(raw) as { role?: string };
            return {
                isSellerAdmin: p.role === 'SELLER_ADMIN',
                isSuperAdmin: p.role === 'SUPER_ADMIN',
            };
        } catch {
            return { isSellerAdmin: false, isSuperAdmin: false };
        }
    }, [location.pathname]);

    const sellerIdForCard = product?.sellerId ?? null;
    const { data: sellerDetail, isLoading: sellerDetailLoading, isError: sellerDetailError } = useQuery({
        queryKey: ['seller', sellerIdForCard],
        queryFn: () => sellersApi.getById(sellerIdForCard!),
        enabled: Boolean(isSuperAdmin && sellerIdForCard),
    });

    const [showDiscountModal, setShowDiscountModal] = useState(false);
    const [isAddToSetModalOpen, setIsAddToSetModalOpen] = useState(false);
    const [showAddVariantModal, setShowAddVariantModal] = useState(false);
    const [showManageVariantsModal, setShowManageVariantsModal] = useState(false);
    const [showAddToCategoryModal, setShowAddToCategoryModal] = useState(false);
    const [showEditProductModal, setShowEditProductModal] = useState(false);
    const [infoTab, setInfoTab] = useState<'description' | 'composition' | 'specs'>('description');
    const [previewImageId, setPreviewImageId] = useState<number | null>(null);
    const thumbsTrackRef = useRef<HTMLDivElement>(null);
    const [isDeleteCategoryModalOpen, setIsDeleteCategoryModalOpen] = useState(false);
    const [categoryToRemove, setCategoryToRemove] = useState<{ id: number; name: string } | null>(null);
    const [isDeleteProductModalOpen, setIsDeleteProductModalOpen] = useState(false);
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const setApproval = useSetProductApproval();
    const setOutOfStockMutation = useSetProductFlag(ProductFlagType.OUT_OF_STOCK);
    const deleteMutation = useDeleteProduct();
    const removeFromCategory = useRemoveFromCategory();

    // Получаем информацию о предыдущей странице
    const fromPath = location.state?.from as string | undefined;
    const fromOrder = location.state?.from === 'order';
    const orderId = location.state?.orderId as number | undefined;

    // Парсим путь для определения типа подборки
    const customSetMatch = fromPath?.match(/^\/sets\/(\d+)$/);
    const filterSetMatch = fromPath?.match(/^\/sets\/filter\/(\d+)$/);

    const customSetId = customSetMatch ? parseInt(customSetMatch[1], 10) : undefined;
    const filterSetId = filterSetMatch ? parseInt(filterSetMatch[1], 10) : undefined;

    // Получаем данные подборок, если путь указывает на подборку
    const { data: customSet } = useCustomSet(customSetId || 0);
    const { data: filterSet } = useFilterSet(filterSetId || 0);

    // Формируем breadcrumbs на основе предыдущей страницы
    const breadcrumbs: BreadcrumbItem[] = useMemo(() => {
        const crumbs: BreadcrumbItem[] = [
            { label: 'Главная', path: '/' },
        ];

        // Если перешли со страницы категории, добавляем breadcrumbs категории
        if (fromPath) {
            // Проверяем, является ли путь страницей категории
            const popularMatch = fromPath.match(/^\/sections\/popular\/categories\/(\d+)\/products$/);
            const withoutSectionsMatch = fromPath.match(/^\/sections\/without-sections\/categories\/(\d+)\/products$/);
            const sectionMatch = fromPath.match(/^\/sections\/(\d+)\/categories\/(\d+)\/products$/);
            const categoryMatch = fromPath.match(/^\/categories\/(\d+)\/products$/);
            const productsPageMatch = fromPath === '/products';
            const productMatch = fromPath.match(/^\/products\/(\d+)$/);

            if (productsPageMatch) {
                // Переход со страницы товаров
                crumbs.push({ label: 'Товары', path: '/products' });
            } else if (customSetMatch && customSet) {
                // Переход с кастомной подборки
                crumbs.push({ label: 'Подборки', path: '/sets' });
                crumbs.push({
                    label: customSet.name,
                    path: `/sets/${customSet.id}`
                });
            } else if (filterSetMatch && filterSet) {
                // Переход с подборки по фильтрам
                crumbs.push({ label: 'Подборки', path: '/sets' });
                crumbs.push({
                    label: filterSet.title,
                    path: `/sets/filter/${filterSet.id}`
                });
            } else if (popularMatch) {
                // Популярные категории
                const categoryId = parseInt(popularMatch[1], 10);
                const category = categories.find(c => c.id === categoryId);

                crumbs.push({ label: 'Категории', path: '/sections' });
                crumbs.push({ label: 'Популярные', path: '/sections/popular' });
                if (category) {
                    crumbs.push({
                        label: category.name,
                        path: `/sections/popular/categories/${category.id}/products`
                    });
                }
            } else if (withoutSectionsMatch) {
                // Категории без секций
                const categoryId = parseInt(withoutSectionsMatch[1], 10);
                const category = categories.find(c => c.id === categoryId);

                crumbs.push({ label: 'Категории', path: '/sections' });
                crumbs.push({ label: 'Без секции', path: '/sections/without-sections' });
                if (category) {
                    crumbs.push({
                        label: category.name,
                        path: `/sections/without-sections/categories/${category.id}/products`
                    });
                }
            } else if (sectionMatch) {
                // Обычная секция
                const sectionId = parseInt(sectionMatch[1], 10);
                const categoryId = parseInt(sectionMatch[2], 10);
                const section = sections.find(s => s.id === sectionId);
                const category = categories.find(c => c.id === categoryId);

                crumbs.push({ label: 'Категории', path: '/sections' });
                if (section) {
                    crumbs.push({
                        label: section.name,
                        path: `/sections/${section.id}/categories`
                    });
                }
                if (category) {
                    crumbs.push({
                        label: category.name,
                        path: `/sections/${sectionId}/categories/${category.id}/products`
                    });
                }
            } else if (categoryMatch) {
                // Категория без секции (старый формат пути)
                const categoryId = parseInt(categoryMatch[1], 10);
                const category = categories.find(c => c.id === categoryId);

                crumbs.push({ label: 'Категории', path: '/sections' });
                crumbs.push({ label: 'Без секции', path: '/sections/without-sections' });
                if (category) {
                    crumbs.push({
                        label: category.name,
                        path: `/sections/without-sections/categories/${category.id}/products`
                    });
                }
            } else if (productMatch && product) {
                // Переход между вариантами товара - используем категорию из данных товара
                // Берем первую категорию товара для построения breadcrumbs
                const productCategory = product.categories && product.categories.length > 0
                    ? product.categories[0].category
                    : product.category;

                if (productCategory) {
                    const category = categories.find(c => c.id === productCategory.id);
                    if (category) {
                        // Определяем, есть ли у категории секция
                        const categorySection = sections.find(s => s.id === category.sectionId);

                        crumbs.push({ label: 'Категории', path: '/sections' });

                        // Проверяем, является ли категория популярной (через проверку секции)
                        // Или используем секцию категории
                        if (categorySection) {
                            crumbs.push({
                                label: categorySection.name,
                                path: `/sections/${categorySection.id}/categories`
                            });
                            crumbs.push({
                                label: category.name,
                                path: `/sections/${categorySection.id}/categories/${category.id}/products`
                            });
                        } else {
                            crumbs.push({ label: 'Без секции', path: '/sections/without-sections' });
                            crumbs.push({
                                label: category.name,
                                path: `/sections/without-sections/categories/${category.id}/products`
                            });
                        }
                    }
                }
            }
        } else if (product) {
            // Если нет fromPath, но есть товар, пытаемся построить breadcrumbs из категории товара
            const productCategory = product.categories && product.categories.length > 0
                ? product.categories[0].category
                : product.category;

            if (productCategory) {
                const category = categories.find(c => c.id === productCategory.id);
                if (category) {
                    const categorySection = sections.find(s => s.id === category.sectionId);

                    crumbs.push({ label: 'Категории', path: '/sections' });

                    if (categorySection) {
                        crumbs.push({
                            label: categorySection.name,
                            path: `/sections/${categorySection.id}/categories`
                        });
                        crumbs.push({
                            label: category.name,
                            path: `/sections/${categorySection.id}/categories/${category.id}/products`
                        });
                    } else {
                        crumbs.push({ label: 'Без секции', path: '/sections/without-sections' });
                        crumbs.push({
                            label: category.name,
                            path: `/sections/without-sections/categories/${category.id}/products`
                        });
                    }
                }
            }
        }

        // Добавляем название товара в конец breadcrumbs
        if (product) {
            crumbs.push({ label: product.name });
        }

        return crumbs;
    }, [fromPath, categories, sections, product, customSet, filterSet, customSetMatch, filterSetMatch])

    const handleBack = () => {
        // Если перешли с отзывов, возвращаемся к отзывам
        if (location.state?.from === 'reviews') {
            navigate('/reviews');
            return;
        }
        // Если перешли из заказа, возвращаемся к заказу
        if (fromOrder && orderId) {
            navigate(`/orders/${orderId}`);
            return;
        }

        // Находим предпоследний breadcrumb (последний - это текущая страница товара)
        if (breadcrumbs.length > 1) {
            const previousBreadcrumb = breadcrumbs[breadcrumbs.length - 2];
            if (previousBreadcrumb.path) {
                navigate(previousBreadcrumb.path);
            } else {
                // Если у предыдущего breadcrumb нет path, переходим на главную
                navigate('/');
            }
        } else {
            // Если breadcrumbs только один (Главная), переходим на главную
            navigate('/');
        }
    };



    if (productLoading) {
        return (
            <div className="page-container">
                <Breadcrumbs items={breadcrumbs} />
                <div className="empty-container">
                    <Loader />
                </div>
            </div>
        );
    }

    if (productError) {
        return (
            <div className="page-container">
                <Breadcrumbs items={breadcrumbs} />
                <div className="empty-container">
                    <RefetchButton
                        refetch={() => refetchProduct()}
                        isLoading={productLoading}
                    />
                </div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="page-container">
                <Breadcrumbs items={breadcrumbs} />
                <div className="empty-container">
                    <div>Товар не найден</div>
                </div>
            </div>
        );
    }



    const handleSetOutOfStock = async () => {
        if (!productIdNum) return;
        try {
            await setOutOfStockMutation.mutateAsync({
                id: productIdNum,
                value: !product?.isOutOfStock
            });
            notifications.show({
                title: 'Успешно',
                message: product?.isOutOfStock ? 'Товар помечен как в наличии' : 'Товар помечен как закончившийся',
                color: 'green',
            });
        } catch (error) {
            notifications.show({
                title: 'Ошибка',
                message: 'Ошибка при изменении статуса товара',
                color: 'red',
            });
        }
    };

    const handleEdit = () => {
        if (!productIdNum) return;
        setShowEditProductModal(true);
    };

    const handleEditProductSuccess = () => {
        // Модалка закроется автоматически, данные продукта обновятся через query invalidation
    };

    const confirmDelete = async () => {
        if (!productIdNum) return;
        try {
            // Получаем список ID из вариантов до удаления
            const variantIds = product?.variants && product.variants.length > 0
                ? product.variants.map(v => v.id)
                : [];

            // Получаем список ID подборок, в которых находится товар
            const customSetIds = product?.customSets && product.customSets.length > 0
                ? product.customSets.map(csp => csp.customSetId)
                : [];

            await deleteMutation.mutateAsync(productIdNum);

            // Инвалидируем кэш для всех продуктов из вариантов
            if (variantIds.length > 0) {
                variantIds.forEach(variantId => {
                    queryClient.invalidateQueries({ queryKey: ['product', variantId] });
                });
            }

            // Инвалидируем и перезапрашиваем конкретные подборки, в которых был товар
            if (customSetIds.length > 0) {
                customSetIds.forEach(customSetId => {
                    // Инвалидируем список продуктов подборки
                    queryClient.invalidateQueries({ queryKey: ['customSetProducts', customSetId] });
                    // Инвалидируем данные подборки (используется в CustomSetProductsPage)
                    queryClient.invalidateQueries({ queryKey: ['customSet', customSetId] });
                });
            }

            notifications.show({
                title: 'Успешно',
                message: 'Товар успешно удален',
                color: 'green',
            });
            setIsDeleteProductModalOpen(false);

            // Возвращаемся на предыдущий breadcrumb
            if (breadcrumbs.length > 1) {
                const previousBreadcrumb = breadcrumbs[breadcrumbs.length - 2];
                if (previousBreadcrumb.path) {
                    navigate(previousBreadcrumb.path);
                } else {
                    // Если у предыдущего breadcrumb нет path, переходим на главную
                    navigate('/');
                }
            } else {
                // Если breadcrumbs только один (Главная), переходим на главную
                navigate('/');
            }
        } catch (error) {
            notifications.show({
                title: 'Ошибка',
                message: 'Ошибка при удалении товара',
                color: 'red',
            });
        }
    };

    const cancelDelete = () => {
        setIsDeleteProductModalOpen(false);
    };

    const handleRemoveFromCategory = (categoryId: number, categoryName: string, e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setCategoryToRemove({ id: categoryId, name: categoryName });
        setIsDeleteCategoryModalOpen(true);
    };

    const confirmRemoveFromCategory = async () => {
        if (!categoryToRemove || !productIdNum) return;

        try {
            await removeFromCategory.mutateAsync({
                productId: productIdNum,
                categoryId: categoryToRemove.id,
            });

            notifications.show({
                title: 'Успешно',
                message: 'Товар успешно удален из категории',
                color: 'green',
            });

            setIsDeleteCategoryModalOpen(false);
            setCategoryToRemove(null);
        } catch (error: any) {
            console.error('Ошибка при удалении товара из категории:', error);
            notifications.show({
                title: 'Ошибка',
                message: error?.response?.data?.message || 'Ошибка при удалении товара из категории',
                color: 'red',
            });
        }
    };

    const cancelRemoveFromCategory = () => {
        setIsDeleteCategoryModalOpen(false);
        setCategoryToRemove(null);
    };

    const handleOpenSellerPage = () => {
        if (sellerIdForCard == null || !product) return;
        navigate(`/sellers/${sellerIdForCard}`, {
            state: {
                returnToProductId: product.id,
                ...(fromPath ? { returnToProductFrom: fromPath } : {}),
            },
        });
    };

    const price = typeof product.price === 'string' ? parseFloat(product.price) : product.price;
    const hasDiscount = !!(product.discount && product.discountedPrice);
    const productImages = product.images ?? [];
    const heroImage =
        productImages.find((img) => img.id === previewImageId) ?? productImages[0] ?? null;
    const activePreviewId = heroImage?.id ?? null;

    const categoryChips =
        product.categories && product.categories.length > 0
            ? product.categories.map((item) => ({
                id: item.category?.id ?? item.categoryId,
                name: item.category?.name ?? `Категория #${item.categoryId}`,
                sectionName: item.category?.sections?.find((s) => s.section?.name)?.section?.name ?? null,
            }))
            : product.category
                ? [{
                    id: product.category.id,
                    name: product.category.name,
                    sectionName: product.category.sections?.find((s) => s.section?.name)?.section?.name ?? null,
                }]
                : [];

    const scrollThumbs = (direction: -1 | 1) => {
        thumbsTrackRef.current?.scrollBy({
            top: direction * 72,
            behavior: 'smooth',
        });
    };

    return (
        <div className={`page-container ${styles.productPage}`}>
            <Breadcrumbs items={breadcrumbs} />
            <div className="page-header">
                <div className={styles.pageHeaderTop}>
                    <div className={styles.productHeaderTitleContainer}>
                        <div className={styles.productHeaderBack}>
                            <BackButton onClick={handleBack} />
                            {fromOrder && (
                                <Button
                                    variant="secondary"
                                    onClick={handleBack}
                                    style={{ padding: '4px 8px', height: 'auto' }}
                                >
                                    Назад к заказу
                                </Button>
                            )}
                        </div>
                        <h1 title={product.name}>{product.name}</h1>
                    </div>
                </div>
            </div>

            <div className={styles.productMain}>
                <div className={styles.productDetails}>
                    {productImages.length > 0 && heroImage && (
                        <div className={styles.productGallery}>
                            {productImages.length > 1 && (
                                <div className={styles.productThumbsCol}>
                                    <button
                                        type="button"
                                        className={styles.productThumbNav}
                                        onClick={() => scrollThumbs(-1)}
                                        aria-label="Предыдущие превью"
                                    >
                                        <IoChevronUp size={18} aria-hidden />
                                    </button>
                                    <div ref={thumbsTrackRef} className={styles.productThumbs}>
                                        {productImages.map((image, index) => (
                                            <button
                                                key={image.id}
                                                type="button"
                                                className={`${styles.productImageItemWrapper} ${image.id === activePreviewId
                                                    ? styles.productImageItemWrapperSelected
                                                    : ''
                                                    }`}
                                                aria-pressed={image.id === activePreviewId}
                                                aria-label={`Превью ${index + 1}`}
                                                onClick={() => setPreviewImageId(image.id)}
                                            >
                                                <div
                                                    className={`${styles.productImageItem} ${image.id === activePreviewId
                                                        ? styles.productImageItemActive
                                                        : ''
                                                        }`}
                                                >
                                                    <CachedImage
                                                        src={productThumbSrc(image) || image.url}
                                                        alt={product.name}
                                                        loading="lazy"
                                                    />
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.productThumbNav}
                                        onClick={() => scrollThumbs(1)}
                                        aria-label="Следующие превью"
                                    >
                                        <IoChevronDown size={18} aria-hidden />
                                    </button>
                                </div>
                            )}
                            <div className={styles.productHero}>
                                <CachedImage
                                    src={heroImage.url}
                                    alt={product.name}
                                    loading="lazy"
                                />
                            </div>
                        </div>
                    )}

                    <div className={styles.productAside}>
                        {product.sellerId != null && (product.approved === null || product.approved === false) && (
                            <div className={styles.productAlerts}>
                                {product.approved === null && (
                                    <div className={`${styles.moderationBanner} ${styles.moderationBannerPending}`}>
                                        <p className={styles.moderationBannerTitle}>На модерации</p>
                                        <p className={styles.moderationBannerText}>
                                            Товар продавца ожидает проверки.
                                        </p>
                                    </div>
                                )}
                                {product.approved === false && (
                                    <div className={`${styles.moderationBanner} ${styles.moderationBannerRejected}`}>
                                        <p className={styles.moderationBannerTitle}>Товар отклонён</p>
                                        <p className={styles.moderationBannerText}>
                                            <strong>Причина:</strong>{' '}
                                            {product.moderationRejectionReason?.trim() || 'Причина не указана.'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className={styles.productInfo}>
                            <div className={styles.productActions} role="toolbar" aria-label="Действия с товаром">
                                {product.sellerId != null && product.approved === null && (
                                    <>
                                        <button
                                            type="button"
                                            className={`${styles.productActionChip} ${styles.productActionChipAccent}`}
                                            disabled={setApproval.isPending}
                                            onClick={() =>
                                                setApproval.mutate(
                                                    { id: product.id, approved: true },
                                                    {
                                                        onError: (err) => {
                                                            notifications.show({
                                                                title: 'Ошибка',
                                                                message:
                                                                    err instanceof Error
                                                                        ? err.message
                                                                        : 'Не удалось одобрить',
                                                                color: 'red',
                                                            });
                                                        },
                                                    },
                                                )
                                            }
                                        >
                                            Одобрить
                                        </button>
                                        <button
                                            type="button"
                                            className={`${styles.productActionChip} ${styles.productActionChipDanger}`}
                                            disabled={setApproval.isPending}
                                            onClick={() => {
                                                setRejectModalOpen(true);
                                            }}
                                        >
                                            Отклонить
                                        </button>
                                    </>
                                )}
                                <button
                                    type="button"
                                    className={styles.productActionChip}
                                    onClick={() => setIsAddToSetModalOpen(true)}
                                >
                                    <IoAdd size={16} aria-hidden />
                                    В подборку
                                </button>
                                {isSellerAdmin && (
                                    <button
                                        type="button"
                                        className={styles.productActionChip}
                                        onClick={() => setShowAddVariantModal(true)}
                                    >
                                        Добавить вариант
                                    </button>
                                )}
                                {isSellerAdmin && (
                                    <button
                                        type="button"
                                        className={styles.productActionChip}
                                        onClick={() => setShowAddToCategoryModal(true)}
                                    >
                                        В категорию
                                    </button>
                                )}
                                {isSellerAdmin && (
                                    <button
                                        type="button"
                                        className={styles.productActionChip}
                                        onClick={() => setShowDiscountModal(true)}
                                    >
                                        <IoPricetagOutline size={16} aria-hidden />
                                        {hasDiscount ? 'Изм. скидку' : 'Скидка'}
                                    </button>
                                )}
                                {isSellerAdmin && (
                                    <button
                                        type="button"
                                        className={styles.productActionChip}
                                        onClick={handleSetOutOfStock}
                                        disabled={setOutOfStockMutation.isPending}
                                    >
                                        {product.isOutOfStock ? 'В наличии' : 'Закончился'}
                                    </button>
                                )}
                                {isSellerAdmin && (
                                    <button
                                        type="button"
                                        className={styles.productActionChip}
                                        onClick={handleEdit}
                                    >
                                        <IoCreateOutline size={16} aria-hidden />
                                        Редактировать
                                    </button>
                                )}
                            </div>

                            <section className={styles.card}>
                                <div className={styles.priceBlock}>
                                    <div className={styles.summaryTitleRow}>
                                        <h2 className={styles.summaryName}>{product.name}</h2>
                                        <div className={styles.summaryCategoryChips}>
                                            {categoryChips.length === 0 ? (
                                                <span className={styles.summaryCategoryChipMuted}>
                                                    Без категории
                                                </span>
                                            ) : (
                                                categoryChips.map((chip) => (
                                                    <span key={chip.id} className={styles.summaryCategoryChip}>
                                                        {chip.sectionName && (
                                                            <>
                                                                <span className={styles.summaryCategoryChipSection}>
                                                                    {chip.sectionName}
                                                                </span>
                                                                <span aria-hidden>•</span>
                                                            </>
                                                        )}
                                                        {chip.name}
                                                        {isSellerAdmin && (
                                                            <button
                                                                type="button"
                                                                className={styles.summaryCategoryChipRemove}
                                                                title="Удалить из категории"
                                                                disabled={removeFromCategory.isPending}
                                                                onClick={(e) =>
                                                                    handleRemoveFromCategory(chip.id, chip.name, e)
                                                                }
                                                            >
                                                                ×
                                                            </button>
                                                        )}
                                                    </span>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                    <div className={styles.summaryStats}>
                                        <span className={styles.summaryStat} title="ID">
                                            #{product.id}
                                        </span>
                                        <span className={styles.summaryStat} title="Рейтинг">
                                            ★ {product.rating != null ? Number(product.rating) : '—'}
                                        </span>
                                        <span className={styles.summaryStat} title="Куплено">
                                            <IoCartOutline size={14} aria-hidden />
                                            {product.purchaseCount ?? 0}
                                        </span>
                                        <span className={styles.summaryStat} title="Просмотрено">
                                            <IoEyeOutline size={14} aria-hidden />
                                            {product.viewedCount ?? 0}
                                        </span>
                                    </div>
                                    <ProductPrice
                                        price={price}
                                        discount={product.discount}
                                        discountedPrice={product.discountedPrice}
                                    />
                                    <ProductBadges
                                        productId={product.id}
                                        hasDiscount={hasDiscount}
                                        isPopular={product.isPopular}
                                        isNew={product.isNew}
                                        isHidden={product.isHidden}
                                        isOutOfStock={product.isOutOfStock}
                                        promotionEnabled={Boolean(product.promotionEnabled)}
                                        customSets={product.customSets?.map((csp) => ({
                                            id: csp.customSetId,
                                            name: csp.customSet?.name || `Подборка #${csp.customSetId}`,
                                        }))}
                                        direction="row"
                                    />
                                </div>
                            </section>

                            {isSuperAdmin && sellerIdForCard != null && (
                                sellerDetailLoading ? (
                                    <Loader size="sm" />
                                ) : sellerDetail ? (
                                    (() => {
                                        const app = sellerDetail.application as
                                            | { activityDescription?: string | null; logoFileUrl?: string | null }
                                            | null;
                                        const description = app?.activityDescription?.trim() || null;
                                        const logoUrl = app?.logoFileUrl?.trim() || null;
                                        return (
                                            <div
                                                className={styles.productSellerCard}
                                                onClick={handleOpenSellerPage}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        handleOpenSellerPage();
                                                    }
                                                }}
                                                role="button"
                                                tabIndex={0}
                                            >
                                                {logoUrl ? (
                                                    <div className={styles.productSellerLogo}>
                                                        <CachedImage
                                                            src={logoUrl}
                                                            alt={sellerDetail.shopName}
                                                            loading="lazy"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className={styles.productSellerLogoPlaceholder}>
                                                        {sellerDetail.shopName.slice(0, 1).toUpperCase()}
                                                    </div>
                                                )}
                                                <div className={styles.productSellerCardBody}>
                                                    <div className={styles.productSellerName}>{sellerDetail.shopName}</div>
                                                    {description && (
                                                        <div className={styles.productSellerDescription}>{description}</div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })()
                                ) : (
                                    <div
                                        className={styles.productSellerCard}
                                        onClick={handleOpenSellerPage}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                handleOpenSellerPage();
                                            }
                                        }}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <div className={styles.productSellerCardBody}>
                                            <div className={styles.productSellerName}>
                                                Перейти к карточке продавца #{sellerIdForCard}
                                            </div>
                                            {sellerDetailError && (
                                                <div className={styles.productSellerDescription}>
                                                    Не удалось загрузить данные — откройте страницу продавца.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )
                            )}

                            <section className={styles.card}>
                                <div className={styles.infoTabs} role="tablist" aria-label="Информация о товаре">
                                    {(
                                        [
                                            ['description', 'Описание'],
                                            ['composition', 'Состав'],
                                            ['specs', 'Характеристики'],
                                        ] as const
                                    ).map(([id, label]) => (
                                        <button
                                            key={id}
                                            type="button"
                                            role="tab"
                                            aria-selected={infoTab === id}
                                            className={`${styles.infoTab} ${infoTab === id ? styles.infoTabActive : ''}`}
                                            onClick={() => setInfoTab(id)}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                <div className={styles.cardBody} role="tabpanel">
                                    {infoTab === 'description' && (
                                        <p className={styles.descriptionText}>
                                            {product.description?.trim() ? (
                                                product.description
                                            ) : (
                                                <span className={styles.rowValueMuted}>Нет описания</span>
                                            )}
                                        </p>
                                    )}
                                    {infoTab === 'composition' && (
                                        <>
                                            <ProductField label="Сборка">
                                                {product.assemblyTimeMinutes != null && product.assemblyTimeMinutes > 0 ? (
                                                    `${product.assemblyTimeMinutes} мин`
                                                ) : (
                                                    <span className={styles.rowValueMuted}>—</span>
                                                )}
                                            </ProductField>
                                            <ProductField label="Состав">
                                                {(() => {
                                                    if (
                                                        !product.composition ||
                                                        !Array.isArray(product.composition) ||
                                                        product.composition.length === 0
                                                    ) {
                                                        return <span className={styles.rowValueMuted}>—</span>;
                                                    }
                                                    const items = product.composition.filter(
                                                        (item: { value?: string; count?: number }) => item?.value?.trim(),
                                                    );
                                                    if (items.length === 0) {
                                                        return <span className={styles.rowValueMuted}>—</span>;
                                                    }
                                                    return (
                                                        <ul className={styles.compositionList}>
                                                            {items.map(
                                                                (item: { value: string; count: number }, i: number) => (
                                                                    <li key={i} className={styles.compositionItem}>
                                                                        <span className={styles.compositionValue}>
                                                                            {item.value}
                                                                        </span>
                                                                        <span className={styles.compositionCount}>
                                                                            {item.count != null && item.count > 0
                                                                                ? item.count
                                                                                : '—'}
                                                                        </span>
                                                                    </li>
                                                                ),
                                                            )}
                                                        </ul>
                                                    );
                                                })()}
                                            </ProductField>
                                        </>
                                    )}
                                    {infoTab === 'specs' && (
                                        <>
                                            <ProductField label="Высота">
                                                {product.height ? (
                                                    `${product.height} см`
                                                ) : (
                                                    <span className={styles.rowValueMuted}>—</span>
                                                )}
                                            </ProductField>
                                            <ProductField label="Ширина">
                                                {product.width ? (
                                                    `${product.width} см`
                                                ) : (
                                                    <span className={styles.rowValueMuted}>—</span>
                                                )}
                                            </ProductField>
                                            <ProductField label="Вес">
                                                {product.weight != null ? (
                                                    `${product.weight} г`
                                                ) : (
                                                    <span className={styles.rowValueMuted}>—</span>
                                                )}
                                            </ProductField>
                                            <ProductField label="Типы цветков">
                                                {product.flowerTypes && product.flowerTypes.length > 0
                                                    ? product.flowerTypes
                                                        .map((pft) => pft.flowerType?.name)
                                                        .filter(Boolean)
                                                        .join(', ')
                                                    : product.flowerType?.name || (
                                                        <span className={styles.rowValueMuted}>—</span>
                                                    )}
                                            </ProductField>
                                            <ProductField label="Кол-во цветов">
                                                {product.flowerCount || (
                                                    <span className={styles.rowValueMuted}>—</span>
                                                )}
                                            </ProductField>
                                            <ProductField label="Цвета">
                                                {product.colors && product.colors.length > 0
                                                    ? product.colors
                                                        .map((pc) => pc.color?.name || '')
                                                        .filter(Boolean)
                                                        .join(', ') || (
                                                        <span className={styles.rowValueMuted}>—</span>
                                                    )
                                                    : (
                                                        <span className={styles.rowValueMuted}>—</span>
                                                    )}
                                            </ProductField>
                                            <ProductField label="Тип сборки">
                                                {product.assemblyType?.name || (
                                                    <span className={styles.rowValueMuted}>—</span>
                                                )}
                                            </ProductField>
                                        </>
                                    )}
                                </div>
                            </section>
                        </div>
                    </div>
                </div>

                {product.variants && product.variants.length > 0 && (
                    <div className={styles.productVariantsSection}>
                        <div className={styles.productVariantsHeader}>
                            <h3>Варианты товара</h3>
                            {isSellerAdmin && (
                                <Button
                                    onClick={() => setShowManageVariantsModal(true)}
                                    variant="secondary"
                                >
                                    Настроить
                                </Button>
                            )}
                        </div>
                        <div className={styles.productVariantsList}>
                            {product.variants.map((variant) => {
                                const variantFirstImage =
                                    variant.images && variant.images.length > 0 ? variant.images[0] : null;
                                const isCurrentVariant = variant.id === productIdNum;
                                const nextFromPath =
                                    fromPath && !fromPath.match(/^\/products\/(\d+)$/) ? fromPath : undefined;
                                return (
                                    <div
                                        key={variant.id}
                                        className={`${styles.productVariantItem} ${isCurrentVariant ? styles.productVariantItemActive : ''}`}
                                        onClick={
                                            isCurrentVariant
                                                ? undefined
                                                : () =>
                                                    navigate(`/products/${variant.id}`, {
                                                        state: { from: nextFromPath },
                                                    })
                                        }
                                        style={{ cursor: isCurrentVariant ? 'default' : 'pointer' }}
                                    >
                                        {variantFirstImage && (
                                            <div className={styles.productVariantImage}>
                                                <CachedImage
                                                    src={productThumbSrc(variantFirstImage) || variantFirstImage.url}
                                                    alt={variant.name}
                                                    loading="lazy"
                                                />
                                            </div>
                                        )}
                                        <div className={styles.productVariantInfo}>
                                            <div className={styles.productVariantName}>
                                                {variant.variantName || variant.name}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

            </div>

            {product && isSellerAdmin && (
                <SetDiscountModal
                    isOpen={showDiscountModal}
                    onClose={() => setShowDiscountModal(false)}
                    productId={productIdNum!}
                    product={product}
                />
            )}

            <AddToSetModal
                isOpen={isAddToSetModalOpen}
                onClose={() => setIsAddToSetModalOpen(false)}
                productIds={productIdNum ? [productIdNum] : []}
            />

            {isSellerAdmin && (
                <AddVariantModal
                    isOpen={showAddVariantModal}
                    onClose={() => setShowAddVariantModal(false)}
                    sourceProductId={productIdNum!}
                />
            )}

            {isSellerAdmin && (
                <ManageVariantsModal
                    isOpen={showManageVariantsModal}
                    onClose={() => setShowManageVariantsModal(false)}
                    productId={productIdNum!}
                />
            )}

            {isSellerAdmin && (
                <AddToCategoryModal
                    isOpen={showAddToCategoryModal}
                    onClose={() => setShowAddToCategoryModal(false)}
                    productId={productIdNum}
                    currentCategoryIds={
                        product?.categories && product.categories.length > 0
                            ? product.categories.map(c => c.categoryId)
                            : product?.categoryId
                                ? [product.categoryId]
                                : []
                    }
                />
            )}

            <DoubleCheckModal
                isOpen={isDeleteCategoryModalOpen}
                onClose={cancelRemoveFromCategory}
                title="Удалить из категории"
                message={
                    categoryToRemove
                        ? `Вы уверены, что хотите удалить товар из категории "${categoryToRemove.name}"?`
                        : 'Вы уверены, что хотите удалить товар из категории?'
                }
                secondButtonText="Удалить"
                secondButtonAction={confirmRemoveFromCategory}
                isLoading={removeFromCategory.isPending}
            />

            <DoubleCheckModal
                isOpen={isDeleteProductModalOpen}
                onClose={cancelDelete}
                title="Удалить товар"
                message="Вы уверены, что хотите удалить этот товар? Это действие нельзя отменить."
                secondButtonText="Удалить"
                secondButtonAction={confirmDelete}
                isLoading={deleteMutation.isPending}
            />

            {productIdNum && isSellerAdmin && (
                <EditProductModal
                    isOpen={showEditProductModal}
                    onClose={() => setShowEditProductModal(false)}
                    productId={productIdNum}
                    onSuccess={handleEditProductSuccess}
                />
            )}

            <RejectProductModal
                product={rejectModalOpen ? product : null}
                onClose={() => setRejectModalOpen(false)}
            />

        </div>
    );
}